import { useCallback, useEffect, useState } from 'react';
import { isDateRangeInvalid } from '../utils/dateRange';

/**
 * Zeitraum-Zustand des Dashboards mit Debounce und
 * "letzter gueltiger Zustand"-Semantik.
 *
 * Hintergrund: Jede Aenderung des Zeitraums loest einen Fan-out von fuenf
 * Zeitreihen-Requests aus (vier Charts, davon einer mit zwei Serien).
 * Native date-Inputs feuern beim Tippen pro Teilfeld ein change-Event —
 * ohne Debounce entstuenden daraus dutzende Request-Wellen.
 *
 * BEWUSSTE ENTSCHEIDUNG (Orchestrator, Iteration 3): Der Fan-out von fuenf
 * Requests pro Aenderung BLEIBT bestehen; ein Batch-Endpoint wird NICHT
 * gebaut. MVGen Management ist ein Single-Admin-Tool, der API-Vertrag der
 * Statistik-Endpunkte ist bereits getestet und wird nicht erneut
 * aufgebrochen. Der Debounce begrenzt lediglich die Anzahl der Wellen.
 */

/** Debounce-Fenster der Zeitraum-Felder in Millisekunden. */
export const DATE_RANGE_DEBOUNCE_MS = 400;

/** Ein angewendeter (gueltiger) Zeitraum; leere Werte = Server-Defaults. */
export interface AppliedDateRange {
  readonly from: string;
  readonly to: string;
}

/** Rueckgabe von useDebouncedDateRange. */
export interface DebouncedDateRangeState {
  /** Aktueller Wert des Von-Feldes (unmittelbar, ohne Debounce). */
  readonly fromInput: string;
  /** Aktueller Wert des Bis-Feldes (unmittelbar, ohne Debounce). */
  readonly toInput: string;
  /** Zuletzt angewendeter gueltiger Zeitraum (steuert die Requests). */
  readonly applied: AppliedDateRange;
  /** true, wenn die aktuelle Eingabe ungueltig ist (Von nach Bis). */
  readonly invalid: boolean;
  /** Setzt das Von-Feld (wird debounced angewendet). */
  readonly setFrom: (value: string) => void;
  /** Setzt das Bis-Feld (wird debounced angewendet). */
  readonly setTo: (value: string) => void;
  /** Leert beide Felder und wendet das SOFORT an (kein Debounce). */
  readonly reset: () => void;
}

/** Leerer Zeitraum = Server-Defaults (stabile Referenz). */
const EMPTY_RANGE: AppliedDateRange = { from: '', to: '' };

/**
 * Verwaltet die Zeitraum-Eingabe des Dashboards:
 * - 400 ms Debounce auf from/to
 * - ungueltiger Zeitraum (Von nach Bis) wird NICHT angewendet: die Charts
 *   bleiben auf dem letzten gueltigen Zustand, es werden keine neuen
 *   Requests ausgeloest (ein Rueckfall auf Server-Defaults waere
 *   irrefuehrend, weil dann ploetzlich fremde Daten zu sehen waeren)
 */
export function useDebouncedDateRange(): DebouncedDateRangeState {
  const [fromInput, setFromInput] = useState<string>('');
  const [toInput, setToInput] = useState<string>('');
  const [applied, setApplied] = useState<AppliedDateRange>(EMPTY_RANGE);

  const invalid = isDateRangeInvalid(fromInput, toInput);

  useEffect(() => {
    if (invalid) {
      // Ungueltig: letzten gueltigen Zeitraum beibehalten, nichts nachladen
      return undefined;
    }
    const handle = window.setTimeout(() => {
      // Referenz nur wechseln, wenn sich der Zeitraum wirklich aendert;
      // sonst wuerden die Zeitreihen-Hooks unnoetig neu laden.
      setApplied((previous) =>
        previous.from === fromInput && previous.to === toInput
          ? previous
          : { from: fromInput, to: toInput }
      );
    }, DATE_RANGE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [fromInput, toInput, invalid]);

  const reset = useCallback((): void => {
    setFromInput('');
    setToInput('');
    // Sofort anwenden: Der Reset ist eine explizite Nutzeraktion bzw. folgt
    // einem Granularitaetswechsel — hier waere ein Debounce nur Verzoegerung
    // und wuerde eine zweite Request-Welle mit dem alten Zeitraum erzeugen.
    setApplied((previous) =>
      previous.from === '' && previous.to === '' ? previous : EMPTY_RANGE
    );
  }, []);

  return { fromInput, toInput, applied, invalid, setFrom: setFromInput, setTo: setToInput, reset };
}

export default useDebouncedDateRange;
